import * as XLSX from 'xlsx';
import { Half, halfYearMonths, toMonth } from './period';
import { sheetToAoaFullRange } from './readXlsx';
import { ChannelResult, HometaxMonthRow } from './types';

function norm(s: any): string { return String(s ?? '').replace(/\s+/g, ''); }
function num(v: any): number { return Number(String(v ?? '').replace(/,/g, '')) || 0; }

// 홈택스 매출 전자세금계산서(과세) / 전자계산서(면세) 목록을 월별로 집계한다.
// - 헤더 행: col0 === '작성일자'
// - '세액' 열이 있으면 세금계산서(과세), 없으면 계산서(면세)
// - 금액은 '합계금액'(부가세 포함) 사용, 승인번호로 중복(다품목 반복) 제거
export function parseHometaxInvoiceRows(aoa: any[][], year: number, half: Half): ChannelResult<HometaxMonthRow> {
  const target = halfYearMonths(year, half);
  const byMonth = new Map<string, HometaxMonthRow>();

  let headerRow = -1;
  for (let i = 0; i < aoa.length; i++) {
    if (norm(aoa[i]?.[0]) === '작성일자') { headerRow = i; break; }
  }

  if (headerRow >= 0) {
    const header = (aoa[headerRow] || []).map(norm);
    const idx = (name: string) => header.indexOf(norm(name));
    const cDate = idx('작성일자');
    const cAmount = idx('합계금액');
    const cApproval = idx('승인번호');
    const isTaxable = idx('세액') >= 0; // 세금계산서엔 세액 열, 계산서엔 없음
    const seen = new Set<string>();

    for (let i = headerRow + 1; i < aoa.length; i++) {
      const row = aoa[i];
      if (!row) continue;
      const month = toMonth(row[cDate]);
      if (!target.includes(month)) continue;
      const approval = String(row[cApproval] ?? '').trim();
      if (approval) {
        if (seen.has(approval)) continue;
        seen.add(approval);
      }
      const amount = num(row[cAmount]);
      if (amount === 0) continue;
      if (!byMonth.has(month)) byMonth.set(month, { month, exempt: 0, taxable: 0 });
      const mr = byMonth.get(month)!;
      if (isTaxable) mr.taxable += amount; else mr.exempt += amount;
    }
  }

  const rows = Array.from(byMonth.values()).sort((a, b) => a.month.localeCompare(b.month));
  const rollup = rows.map(r => ({ month: r.month, taxable: r.taxable, exempt: r.exempt }));
  return { channel: '홈택스', rows, rollup };
}

export function parseHometaxFile(file: File, year: number, half: Half): Promise<ChannelResult<HometaxMonthRow>> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target!.result as ArrayBuffer);
        const wb = XLSX.read(data, { type: 'array' });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const aoa = sheetToAoaFullRange(ws);
        resolve(parseHometaxInvoiceRows(aoa, year, half));
      } catch (err) { reject(err); }
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsArrayBuffer(file);
  });
}
